import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Icon } from '../../components/Icon.jsx';
import { useT, useLang } from '../../lib/i18n.jsx';
import { get, post, patch } from '../../lib/api.js';
import { useAsync, initials } from '../../lib/hooks.js';
import { formatDateTime, relativeTime } from '../../lib/format.js';
import { toast } from '../../lib/toast.jsx';
import { ConfirmDialog, DetailState, InlineError, formatBytes } from '../hosting/detailUi.jsx';

// Backup data: GET /backups/hosting/{id}, POST /backups/hosting/{id} (manual),
// POST /backups/hosting/{id}/restore/{backup_id}, GET/PATCH /backups/hosting/{id}/settings.
export default function BackupsDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const t = useT();
  const { lang } = useLang();

  const { data: hosting, loading, error, reload: reloadHosting } = useAsync(() => get(`/hostings/${id}`), [id]);
  const backups = useAsync(() => get(`/backups/hosting/${id}?limit=100`), [id]);
  const [tab, setTab] = useState('snapshots');
  const [pending, setPending] = useState(null);

  // Poll while a backup is queued or running so its status updates by itself.
  const busyBackup = (backups.data || []).some((b) => b.status === 'pending' || b.status === 'running');
  useEffect(() => {
    if (!busyBackup) return undefined;
    const timer = setInterval(() => backups.reload(), 10000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busyBackup]);

  if (loading || error || !hosting) {
    return (
      <DetailState
        lang={lang}
        loading={loading}
        error={error}
        backLabel={t('bk.title')}
        onBack={() => navigate('/backups')}
        notFoundTitle={t('backupsDetail.notFound')}
        onRetry={reloadHosting}
      />
    );
  }

  const list = backups.data || [];
  const completed = list.filter((b) => b.status === 'completed');
  const totalBytes = completed.reduce((a, b) => a + (b.size_bytes || 0), 0);
  const lastOk = completed[0];
  const lastFailed = list[0]?.status === 'failed';

  return (
    <main className="page" data-screen-label={`Backups: ${hosting.domain}`}>
      <button className="back-link" onClick={() => navigate('/backups')}>
        <Icon name="arrow-left" size={14} />
        <span style={{ marginLeft: 4 }}>{t('bk.title')}</span>
      </button>

      <div className="detail-head" style={{ flexWrap: 'wrap' }}>
        <div className="fav-lg">{initials(hosting.domain.split('.')[0])}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ overflowWrap: 'anywhere' }}>{hosting.domain}</h1>
          <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className={'status-pill ' + (hosting.backup_enabled && !lastFailed ? '' : 'is-warn')}>
              <span className="dot" />
              {!hosting.backup_enabled
                ? t('backupsDetail.autoOff')
                : lastFailed
                  ? t('backupsDetail.lastFailed')
                  : t('bk.status.healthy')}
            </span>
            <span style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
              {t(completed.length === 1 ? 'backupsDetail.snapshots.one' : 'backupsDetail.snapshots.other', { n: completed.length })}
              {totalBytes > 0 && ` · ${formatBytes(totalBytes, lang)}`}
              {lastOk && ` · ${t('backupsDetail.last', { time: relativeTime(lastOk.completed_at || lastOk.created_at, lang) })}`}
            </span>
          </div>
        </div>
        <button
          className="btn-primary"
          disabled={hosting.status !== 'active' || busyBackup}
          title={hosting.status !== 'active' ? t('backupsDetail.onlyActive') : undefined}
          onClick={() => setPending({ kind: 'create' })}
        >
          <Icon name="rotate" size={13} stroke={2} />
          <span style={{ marginLeft: 6 }}>{busyBackup ? t('backupsDetail.running') : t('bk.new')}</span>
        </button>
      </div>

      <div className="tabs" role="tablist">
        {[
          ['snapshots', t('bk.tab.snapshots')],
          ['settings', t('backupsDetail.tab.schedule')],
        ].map(([k, label]) => (
          <button key={k} role="tab" aria-selected={tab === k} aria-current={tab === k ? 'page' : undefined} onClick={() => setTab(k)}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'snapshots' && (
        <SnapshotsTab
          state={backups}
          lang={lang}
          onRestore={(b) => setPending({ kind: 'restore', b })}
        />
      )}
      {tab === 'settings' && <SettingsTab hostingId={hosting.id} lang={lang} onSaved={reloadHosting} />}

      {pending?.kind === 'create' && (
        <ConfirmDialog
          danger={false}
          lang={lang}
          title={t('backupsDetail.create.title')}
          body={t('backupsDetail.create.body')}
          cta={t('backupsDetail.create.cta')}
          onClose={() => setPending(null)}
          onConfirm={async () => {
            await post(`/backups/hosting/${hosting.id}`, null);
            setPending(null);
            toast(t('backupsDetail.create.done'));
            backups.reload();
          }}
        />
      )}
      {pending?.kind === 'restore' && (
        <ConfirmDialog
          lang={lang}
          title={t('backupsDetail.restore.title')}
          body={t('backupsDetail.restore.body', {
            domain: hosting.domain,
            date: formatDateTime(pending.b.completed_at || pending.b.created_at, lang),
          })}
          cta={t('backupsDetail.restore.cta')}
          onClose={() => setPending(null)}
          onConfirm={async () => {
            await post(`/backups/hosting/${hosting.id}/restore/${pending.b.id}`, null);
            setPending(null);
            toast(t('backupsDetail.restore.done'));
          }}
        />
      )}
    </main>
  );
}

function SnapshotsTab({ state, lang, onRestore }) {
  const t = useT();
  const { data, loading, error } = state;
  const list = data || [];
  const typeLabel = (k) => (SNAPSHOT_TYPES.includes(k) ? t(`backupsDetail.type.${k}`) : k);
  const statusLabel = (k) => (SNAPSHOT_STATUSES.includes(k) ? t(`backupsDetail.status.${k}`) : k);

  return (
    <section className="card" style={{ padding: 0 }}>
      <div className="files-toolbar">
        <div>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{t('bk.snapshots.title')}</h3>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
            {t('backupsDetail.pickSnapshot')}
          </div>
        </div>
      </div>
      {loading && <div style={{ padding: '14px 22px', color: 'var(--ink-3)', fontSize: 13 }}>{t('common.loading')}</div>}
      {error && <div style={{ padding: '0 22px 14px' }}><InlineError error={error} lang={lang} /></div>}
      {!loading && !error && list.length === 0 && (
        <div className="empty-state">
          <div className="iccirc"><Icon name="archive" size={22} /></div>
          <h4>{t('backupsDetail.empty.title')}</h4>
          <p>{t('backupsDetail.empty.body')}</p>
        </div>
      )}
      {list.length > 0 && (
        <div className="bk-snap-head">
          <span>{t('backupsDetail.col.time')}</span>
          <span>{t('bk.snap.col.kind')}</span>
          <span>{t('bk.snap.col.size')}</span>
          <span>{t('bk.col.status')}</span>
          <span>{t('backupsDetail.col.keptUntil')}</span>
          <span />
        </div>
      )}
      {list.map((b) => (
        <div className="bk-snap-row" key={b.id}>
          <div>
            <div style={{ fontSize: 13, color: 'var(--ink)' }}>{formatDateTime(b.completed_at || b.started_at || b.created_at, lang)}</div>
            {b.status === 'failed' && b.error_message && (
              <div style={{ fontSize: 11.5, color: 'var(--danger)', marginTop: 2, overflowWrap: 'anywhere' }}>{b.error_message}</div>
            )}
          </div>
          <div>
            <span className={`bk-kind bk-kind--${b.backup_type === 'manual' ? 'manual' : 'daily'}`}>{typeLabel(b.backup_type)}</span>
          </div>
          <div className="mono" style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>{formatBytes(b.size_bytes, lang)}</div>
          <div>
            <span className={'status-pill ' + (b.status === 'completed' ? '' : b.status === 'failed' ? 'is-danger' : 'is-warn')}>
              <span className="dot" />
              {statusLabel(b.status)}
            </span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{b.expires_at ? formatDateTime(b.expires_at, lang) : '—'}</div>
          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
            {b.status === 'completed' && (
              <button className="btn-ghost" onClick={() => onRestore(b)}>
                <Icon name="rotate" size={13} />
                <span style={{ marginLeft: 6 }}>{t('backupsDetail.restore.cta')}</span>
              </button>
            )}
          </div>
        </div>
      ))}
    </section>
  );
}

const SNAPSHOT_TYPES = ['full', 'incremental', 'manual'];
const SNAPSHOT_STATUSES = ['pending', 'running', 'completed', 'failed'];
const INTERVALS = [4, 8, 24];
const RETENTIONS = [7, 14, 30];

function SettingsTab({ hostingId, lang, onSaved }) {
  const t = useT();
  const { data, loading, error, reload } = useAsync(() => get(`/backups/hosting/${hostingId}/settings`), [hostingId]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const dirty = form && data && (
    form.backup_enabled !== data.backup_enabled ||
    form.backup_interval_hours !== data.backup_interval_hours ||
    form.backup_retention_days !== data.backup_retention_days
  );

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      await patch(`/backups/hosting/${hostingId}/settings`, form);
      toast(t('backupsDetail.saved'));
      reload();
      onSaved?.();
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };

  if (loading || !form) {
    return (
      <section className="card">
        <div style={{ padding: '18px 22px', color: 'var(--ink-3)', fontSize: 13 }}>
          {error ? <InlineError error={error} lang={lang} /> : t('common.loading')}
        </div>
      </section>
    );
  }

  const retentions = Array.from(new Set([...RETENTIONS, form.backup_retention_days])).sort((a, b) => a - b);

  return (
    <section className="card">
      <div className="card-h"><h3>{t('backupsDetail.auto.title')}</h3></div>
      <div style={{ padding: '6px 22px 18px' }}>
        <InlineError error={err} lang={lang} />
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--ink-2)', marginBottom: 16 }}>
          <input
            type="checkbox"
            checked={form.backup_enabled}
            onChange={() => setForm({ ...form, backup_enabled: !form.backup_enabled })}
          />
          <span>{t('backupsDetail.auto.toggle')}</span>
        </label>

        <div className="kv" style={{ alignItems: 'center' }}>
          <span className="k">{t('backupsDetail.frequency')}</span>
          <span className="v">
            <div className="seg seg--sm" role="group" aria-label={t('backupsDetail.frequency')} style={{ flexWrap: 'wrap' }}>
              {INTERVALS.map((h) => (
                <button
                  key={h}
                  type="button"
                  disabled={!form.backup_enabled}
                  aria-pressed={form.backup_interval_hours === h}
                  onClick={() => setForm({ ...form, backup_interval_hours: h })}
                >
                  {h === 24 ? t('bk.set.freq.daily') : t('backupsDetail.everyHours', { n: h })}
                </button>
              ))}
            </div>
          </span>
        </div>
        <div className="kv" style={{ alignItems: 'center' }}>
          <span className="k">{t('backupsDetail.keepFor')}</span>
          <span className="v">
            <div className="seg seg--sm" role="group" aria-label={t('backupsDetail.retention')} style={{ flexWrap: 'wrap' }}>
              {retentions.map((d) => (
                <button
                  key={d}
                  type="button"
                  aria-pressed={form.backup_retention_days === d}
                  onClick={() => setForm({ ...form, backup_retention_days: d })}
                >
                  {t(d === 1 ? 'backupsPage.days.one' : 'backupsPage.days.other', { n: d })}
                </button>
              ))}
            </div>
          </span>
        </div>

        <div style={{ marginTop: 14 }}>
          <button className="btn-primary" disabled={!dirty || busy} onClick={save}>
            {busy ? t('backupsDetail.working') : t('g.save')}
          </button>
        </div>
      </div>
    </section>
  );
}
